import type { CellValue, ColumnGroup } from 'ui/atoms/Table/Table.utils'

/** The data columns to hide: those the name search rules out and, optionally, those without values. */
export function filterHiddenColumnIndices(
  initialValue: CellValue[][],
  nameSearchTerm: string,
  showOnlyColumnsWithMissingValues: boolean,
  emptyColumnFilterMode: 'withMissingValues' | 'completelyEmpty',
  columnGroups?: readonly (ColumnGroup | null)[]
): number[] {
  if (!initialValue?.[0]?.length) {
    return []
  }

  const hiddenColumnIndices: Set<number> = new Set()

  // Filter by search name
  if (nameSearchTerm?.length >= 2) {
    const headers = initialValue[0] || []
    const searchTerm = nameSearchTerm.toLowerCase()
    headers.forEach((header, index) => {
      if (!header || index === 0) {
        return
      }

      const headerLower = header.toLowerCase()
      const groupLower = columnGroups?.[index]?.label.toLowerCase()
      if (headerLower.includes(searchTerm) || groupLower?.includes(searchTerm)) {
        return
      }

      hiddenColumnIndices.add(index)
    })
  }

  // Filter by column value state
  if (showOnlyColumnsWithMissingValues) {
    const nrOfColumns = initialValue[0]?.length || 0

    for (let columnIndex = 1; columnIndex < nrOfColumns; columnIndex++) {
      if (hiddenColumnIndices.has(columnIndex)) {
        continue
      }

      if (emptyColumnFilterMode === 'withMissingValues') {
        // Check rows, if any row has a missing value - keep the column.
        let hasMissingValue = false
        for (let rowIndex = 1; rowIndex < initialValue.length; rowIndex++) {
          const value = initialValue[rowIndex]?.[columnIndex]
          if (value === null || value === '' || value === undefined) {
            hasMissingValue = true
            break
          }
        }

        if (!hasMissingValue) {
          hiddenColumnIndices.add(columnIndex)
        }

        continue
      }

      // Hide columns that are completely empty.
      let hasAnyValue = false
      for (let rowIndex = 1; rowIndex < initialValue.length; rowIndex++) {
        const value = initialValue[rowIndex]?.[columnIndex]
        if (value !== null && value !== '' && value !== undefined) {
          hasAnyValue = true
          break
        }
      }

      if (!hasAnyValue) {
        hiddenColumnIndices.add(columnIndex)
      }
    }
  }

  return Array.from(hiddenColumnIndices)
}

const GROUPED_HEADER_LINE_HEIGHT = 16
const GROUPED_HEADER_VERTICAL_PADDING = 12
const GROUPED_HEADER_HORIZONTAL_PADDING = 8
/** Average width of a `text-xs` semibold character; a 100px column fits twelve. */
const GROUPED_HEADER_CHARACTER_WIDTH = 7.5
/** Tall enough for the time column's two-line header. */
const MIN_GROUPED_HEADER_HEIGHT = 52

function getCharactersPerLine(columnWidth: number): number {
  return Math.max(1, Math.floor((columnWidth - GROUPED_HEADER_HORIZONTAL_PADDING) / GROUPED_HEADER_CHARACTER_WIDTH))
}

function estimateWrappedLineCount(text: string, charactersPerLine: number): number {
  let lineCount = 1
  let lineLength = 0

  for (const word of text.trim().split(/\s+/).filter(Boolean)) {
    // A word wider than a line is hyphenated, so it takes ceil(length / line) lines of its own.
    const wordLines = Math.ceil(word.length / charactersPerLine)
    if (lineLength > 0 && lineLength + 1 + word.length <= charactersPerLine) {
      lineLength += 1 + word.length
      continue
    }
    lineCount += lineLength > 0 ? wordLines : wordLines - 1
    lineLength = word.length % charactersPerLine || charactersPerLine
  }

  return lineCount
}

export type GroupedHeaderLayout = {
  /** Height of the column header row: tall enough to show the longest header in full, by estimate. */
  rowHeight: number
  /** Lines a header title may take before it is cut off with an ellipsis, so a long one is never clipped mid-line. */
  titleLineCount: number
}

export function getGroupedHeaderLayout(headers: readonly CellValue[], columnWidth: number): GroupedHeaderLayout {
  const charactersPerLine = getCharactersPerLine(columnWidth)
  const lineCount = Math.max(
    1,
    ...headers.map((header) =>
      typeof header === 'string' ? estimateWrappedLineCount(header.split('\n')[0], charactersPerLine) : 1
    )
  )
  const rowHeight = Math.max(
    MIN_GROUPED_HEADER_HEIGHT,
    lineCount * GROUPED_HEADER_LINE_HEIGHT + GROUPED_HEADER_VERTICAL_PADDING
  )

  return {
    rowHeight,
    titleLineCount: Math.floor((rowHeight - GROUPED_HEADER_VERTICAL_PADDING) / GROUPED_HEADER_LINE_HEIGHT),
  }
}
